import { useCallback, useEffect, useRef, useState } from 'react';
import ThreeDsService from '@/services/ThreeDsService';
import type {
  Initiate3DsPayload,
  Complete3DsResponse
} from '@/services/ThreeDsService';

const CHALLENGE_TIMEOUT_MS = 5 * 60 * 1000;
const SUCCESS_RESPONSE_CODE = 'SUCCESS';

type ThreeDsState =
  | { phase: 'idle' }
  | { phase: 'initiating' }
  | { phase: 'processing' }
  | {
      phase: 'challenge-hidden';
      iframeUrl: string;
      authkey: string;
      origin: string;
    }
  | {
      phase: 'challenge-visible';
      iframeUrl: string;
      authkey: string;
      origin: string;
    }
  | { phase: 'completing'; authkey: string }
  | { phase: 'success'; result: Complete3DsResponse }
  | { phase: 'declined'; responseCode: string; responseMessage: string }
  | {
      phase: 'error';
      reason: 'internal-error' | 'not-authenticated' | 'network' | 'timeout';
    };

const useThreeDsAuthentication = () => {
  const [state, setState] = useState<ThreeDsState>({ phase: 'idle' });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const clearChallengeTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const completeAuthentication = useCallback(
    async (authkey: string) => {
      clearChallengeTimeout();
      setState({ phase: 'completing', authkey });
      try {
        const { data } = await ThreeDsService.complete3ds(authkey);
        if (!data.success) {
          setState({ phase: 'error', reason: 'not-authenticated' });
          return;
        }
        if (data.data?.responseCode !== SUCCESS_RESPONSE_CODE) {
          setState({
            phase: 'declined',
            responseCode: data.data?.responseCode ?? 'UNKNOWN',
            responseMessage: data.data?.responseMessage ?? ''
          });
          return;
        }
        setState({ phase: 'success', result: data });
      } catch {
        setState({ phase: 'error', reason: 'network' });
      }
    },
    [clearChallengeTimeout]
  );

  useEffect(() => {
    if (
      state.phase !== 'challenge-hidden' &&
      state.phase !== 'challenge-visible'
    ) {
      return;
    }
    const { authkey, origin } = state;

    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      switch (event.data?.type) {
        case 'AuthenticationUserInteractionRequired':
          setState((prev) =>
            prev.phase === 'challenge-hidden' ||
            prev.phase === 'challenge-visible'
              ? { ...prev, phase: 'challenge-visible' }
              : prev
          );
          break;
        case 'AuthenticationUserInteractionFinished':
          setState((prev) =>
            prev.phase === 'challenge-hidden' ||
            prev.phase === 'challenge-visible'
              ? { ...prev, phase: 'challenge-hidden' }
              : prev
          );
          break;
        case 'AuthenticationComplete':
          completeAuthentication(authkey);
          break;
        case 'AuthenticationInternalError':
          clearChallengeTimeout();
          setState({ phase: 'error', reason: 'internal-error' });
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', receiveMessage, false);
    return () => window.removeEventListener('message', receiveMessage);
  }, [state, completeAuthentication, clearChallengeTimeout]);

  useEffect(() => {
    if (state.phase !== 'challenge-visible') return;
    timeoutRef.current = setTimeout(() => {
      setState({ phase: 'error', reason: 'timeout' });
    }, CHALLENGE_TIMEOUT_MS);
    return clearChallengeTimeout;
  }, [state.phase, clearChallengeTimeout]);

  const isInChallenge =
    state.phase === 'challenge-hidden' || state.phase === 'challenge-visible';

  useEffect(() => {
    if (!isInChallenge) return;

    const handlePopState = () => {
      clearChallengeTimeout();
      setState({ phase: 'idle' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isInChallenge, clearChallengeTimeout]);

  const initiate = useCallback(
    async (payload: Initiate3DsPayload) => {
      setState({ phase: 'initiating' });
      try {
        const { data } = await ThreeDsService.initiate3ds(payload);
        if (data.errorMessage || !data.authkey) {
          setState({ phase: 'error', reason: 'internal-error' });
          return;
        }
        if (data.requiresAuthentication && data.iframeUrl) {
          setState({
            phase: 'challenge-hidden',
            iframeUrl: data.iframeUrl,
            authkey: data.authkey,
            origin: new URL(data.iframeUrl).origin
          });
          return;
        }
        setState({ phase: 'processing' });
        await completeAuthentication(data.authkey);
      } catch {
        setState({ phase: 'error', reason: 'network' });
      }
    },
    [completeAuthentication]
  );

  const retry = useCallback(() => {
    clearChallengeTimeout();
    setState({ phase: 'idle' });
  }, [clearChallengeTimeout]);

  return { state, initiate, retry, iframeRef };
};

export default useThreeDsAuthentication;
export type { ThreeDsState };
